import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import workspaceApi from "../api/workspaceApi";
import { Member, TreeDataResponse, TreeNode } from "../../types/workgroup";
import {
  getWorkspaceCode,
  getWorkspaceName,
  setWorkspaceCode,
  setWorkspaceName,
} from "../../shared/utils/auth";

export interface WorkspaceDTO {
  dsdCode: string;
  alias: string;
  myDefaultTenancy: boolean;
  allowIpAccess?: boolean;
  paymentCode: string;
  tenancyCode?: string;
  product?: string;
  paymentInfo: WorkspacePaymentInfo;
  tenancyStatus:
    | "NONE"
    | "INIT"
    | "PAUSE"
    | "RUNNING"
    | "STOP"
    | "WAIT_FOR_DELETE";
  tenancyName?: string;
  createUserId: string;
  masterUserId: string;
  createdTime: number;
  updatedTime: number;
  maxUserCount?: number;
  planInfo?: PlanInfo;
  tenancyUsageSize: number;
  userId?: string | null;
  currentInternalUserCount?: number;
  currentExternalUserCount?: number;
  currentLiteExternalUserCount?: number;
  maxInternalUserCount?: number;
  maxExternalUserCount?: number;
  maxLiteExternalUserCount?: number;
  userStatus?: number;
}
export interface PlanInfo {
  planCode: string;
  planName: string;
  useMobile: boolean;
  useWebView: boolean;
  useMac: boolean;
  storagePerUser: number;
  externalUserPerUser: number;
  priceDollar: number;
  priceWon: number;
  status: string;
  createTime: number;
  updateTime: number;
  baseStorageSize: number;
}
export interface WorkspaceJoinDto {
  dsdCode?: string;
  userId: string;
  userName: string;
  companyName?: string;
  phoneNumber?: string | null;
  password: string;
}

export interface WorkspacePaymentInfo {
  maxExternalUserCount: number;
  maxInternalUserCount: number;
  storagePerUser: number;
  paymentCode: string;
  payUserId: string;
  planCode: string;
  maxUserCount?: number;
  additionalStorage: number;
  payTerm: number;
  payDay: number;
  payDate: number;
  startDate: number;
  workspaceInfo?: WorkspaceDTO;
  price: number;
  serviceEndTime: number;
  serviceStartTime: number;
  subscriptionId?: string;
}

export interface WorkspacePaymentHis {
  paymentHisCode: string;
  paymentCode: string;
  payUserId: string;
  planCode: string;
  maxUserCount: number;
  additionalStorage: number;
  payPrice: number;
  payTerm: number;
  payDay: number;
  payDate: number;
  startDate: number;
  createTime: number;
}

export interface WorkspaceState {
  workspaceSideList?: WorkspaceDTO[];
  workspaceDetail?: WorkspaceDTO;
  myPamentList?: WorkspacePaymentInfo[];
  myPamentHisList?: WorkspacePaymentHis[];
  planList?: PlanInfo[];
  memberList?: workspaceMemberDTO[];
  treeData?: TreeDataResponse;
  externalUserList?: Member[];
  internalUserList?: Member[];
  searchUserList?: Member[];
}

export interface CreateWorkspaceRequestDTO {
  paymentCode: string;
  alias: string;
  masterUserId?: string;
}

export interface UpdateWorkspaceStatusRequestDTO {
  dsdCode: string;
  workspaceStatus:
    | "NONE"
    | "INIT"
    | "PAUSE"
    | "RUNNING"
    | "STOP"
    | "WAIT_FOR_DELETE";
}
export interface workspaceMemberDTO {
  dsdCode?: string;
  userId?: string;
  userName?: string;
  companyName?: string | null;
  deptName?: string | null;
  userType?: number;
  phoneNumber?: string;
  status?: number;
  etc?: string | null;
  logDate?: number;
  regDate?: number;
  defaultTenancyCode?: string;
  email?: string;
}

export interface DeleteWorkspaceRequestDTO {
  dsdCode: string;
}

export interface WorkgroupMemberRequest {
  workgroupId?: string;
  userId?: string;
  searchOpt?: string;
  withdrawYN?: string;
  userType?: number | null;
  userIds?: string[];
  internalPos?: number;
  externalPos?: number;
  dsdCode?: string;
  status?: number;
  isAdminPage?: boolean;
  order?: string;
  tenancyCode?: string;
}

// # initial state
const initialState: WorkspaceState = {
  workspaceSideList: undefined,
  workspaceDetail: undefined,
  myPamentList: undefined,
  myPamentHisList: undefined,
  planList: undefined,
  memberList: undefined,
  treeData: undefined,
  externalUserList: [],
  internalUserList: [],
  searchUserList: [],
};

export const trunkGetWorkspaceList = createAsyncThunk(
  "workspace/list",
  async () => {
    const list = await workspaceApi.getWorkspaceList();
    return list;
  }
);

export const trunkGetWorkspaceDetail = createAsyncThunk(
  "workspace/detail",
  async (dsdCode: string) => {
    const detail = await workspaceApi.getWorkspaceDetail(dsdCode);
    return detail;
  }
);

export const trunkGetWorkspacePayMyList = createAsyncThunk(
  "workspace/pay/payment/mylist",
  async () => {
    const list = await workspaceApi.getWorkspacePayMyList();
    return list;
  }
);

export const trunkGetWorkspacePayMyHisList = createAsyncThunk(
  "workspace/pay/payment/myHistory",
  async () => {
    const list = await workspaceApi.getWorkspacePayMyHistory();
    return list;
  }
);

export const trunkGetWorkspacePlanList = createAsyncThunk(
  "workspace/pay/payment/plans",
  async () => {
    const list = await workspaceApi.getWorkspacePayPlanList();
    return list;
  }
);

export const trunkCreateWorkspace = createAsyncThunk(
  "workspace/create",
  async (req: CreateWorkspaceRequestDTO) => {
    return await workspaceApi.createWorkspace(req);
  }
);

export const trunkUpdateWorkspace = createAsyncThunk(
  "workspace/state",
  async (req: UpdateWorkspaceStatusRequestDTO) => {
    return await workspaceApi.updateWorkspace(req);
  }
);

export const trunkDeleteWorkspace = createAsyncThunk(
  "workspace/delete",
  async (req: DeleteWorkspaceRequestDTO) => {
    return await workspaceApi.deleteWorkspace(req);
  }
);
export const trunkGetWorkspaceMemberList = createAsyncThunk(
  "workspace/member/list",
  async (req: WorkgroupMemberRequest) => {
    return await workspaceApi.getWorkspaceMemberList({
      ...req,
      tenancyCode: getWorkspaceCode() ?? "",
    });
  }
);
export const trunkGetWorkspaceInviteMember = createAsyncThunk(
  "workspace/member/invite",
  async (user: workspaceMemberDTO) => {
    return await workspaceApi.getWorkspaceInviteMember(user);
  }
);
export const trunkGetWorkspaceInviteAgainMembers = createAsyncThunk(
  "workspace/member/invite/resend",
  async (req: { userId: string; dsdCode: string }) => {
    return await workspaceApi.getWorkspaceInviteAgainMember(req);
  }
);
export const trunkSignupWorkspacMember = createAsyncThunk(
  "workspace/member/join",
  async (req: { user: WorkspaceJoinDto; withInvitation: boolean }) => {
    return await workspaceApi.signupWorkspacMember(req);
  }
);
export const trunkUpdateWorkspaceMember = createAsyncThunk(
  "workspace/member/update",
  async (req: workspaceMemberDTO) => {
    return await workspaceApi.updateWorkspaceMember(req);
  }
);
export const trunkVerifyMemberMailAgain = createAsyncThunk(
  "workspace/member/register/verify/resend",
  async (userId: string) => {
    return await workspaceApi.verifyMemberMailAgain(userId);
  }
);
export const trunkGetWorkspaceConfig = createAsyncThunk(
  "workspace/config",
  async () => {
    return await workspaceApi.getWorkspaceConfig();
  }
);
export const trunkGetWorkspaceUserInfo = createAsyncThunk(
  "workspace/workspaceInfo",
  async (dsdCode: string) => {
    return await workspaceApi.getWorkspaceUserInfo(dsdCode);
  }
);
export const trunkVerifyMemberMail = createAsyncThunk(
  "workspace/member/register/verify",
  async (userId: string) => {
    return await workspaceApi.verifyMemberMail(userId);
  }
);
export const trunkDeleteUser = createAsyncThunk(
  "workspace/member/delete",
  async (req: { userIds: string[]; dsdCode: string }) => {
    return await workspaceApi.deleteMember(req);
  }
);
const workspaceSlice = createSlice({
  name: "workspace",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      trunkGetWorkspaceList.fulfilled,
      (state, { payload: list }) => {
        state.workspaceSideList = list;
        const currWorkspaceName = getWorkspaceName();
        if (currWorkspaceName) {
          setWorkspaceName(
            list.filter((item) => item.alias === currWorkspaceName)[0]?.alias ??
              "My Space"
          );
          setWorkspaceCode(
            list.filter((item) => item.alias === currWorkspaceName)[0]
              ?.dsdCode ?? "MTECOCOMMON00001"
          );
        }
      }
    );
    builder.addCase(
      trunkGetWorkspaceDetail.fulfilled,
      (state, { payload: detail }) => {
        // state.workspaceDetail = detail;
        state.workspaceDetail = detail;
        window.localStorage.setItem(
          "fasoo-workspace-info",
          JSON.stringify(detail)
        );
      }
    );
    builder.addCase(
      trunkGetWorkspacePayMyList.fulfilled,
      (state, { payload: detail }) => {
        // state.workspaceDetail = detail;
        state.myPamentList = detail;
      }
    );
    builder.addCase(
      trunkGetWorkspacePayMyHisList.fulfilled,
      (state, { payload: detail }) => {
        // state.workspaceDetail = detail;
        state.myPamentHisList = detail;
      }
    );
    builder.addCase(
      trunkGetWorkspacePlanList.fulfilled,
      (state, { payload: detail }) => {
        // state.workspaceDetail = detail;
        state.planList = detail;
      }
    );
    builder.addCase(trunkGetWorkspaceMemberList.fulfilled, (state, action) => {
      const type =
        action.meta.arg.internalPos && action.meta.arg.internalPos > 0 ? 1 : 2;
      const members = action.payload.map((item) => {
        return {
          ...item,
          userId: item.userId ?? "",
          userName: item.userName ?? "",
          userType: item.userType ?? 1,
          status: item.status ?? 1,
          workgroupCount: "",
          companyCode: null,
          companyName: null,
          deptCode: null,
          deptName: null,
          email: null,
          etc: null,
          inviteTime: 0,
          lastLoginTime: 0,
          linkedYN: null,
          phoneNumber: null,
          positionCode: null,
          positionName: null,
          roleCode: null,
          roleName: null,
          withdrawYN: null,
          workgroupName: "",
        };
      });
      state.searchUserList = members;
      if (action.meta.arg.isAdminPage) {
        state.memberList = action.payload;
      } else if (type === 1) {
        state.internalUserList = members;
        state.treeData = {
          version: "1.0",
          nodes: action.payload.map((item) => {
            return {
              columnText: item.userName ?? item.userId,
              hasChildren: false,
              isPolicySet: false,
              showChildren: false,
              subTree: [],
              type: "user",
              value: item.userId,
            } as TreeNode;
          }),
        };
      } else if (type === 2) {
        state.externalUserList = members;
      }
    });
    builder.addCase(
      trunkGetWorkspaceUserInfo.fulfilled,
      (state, { payload: info }) => {
        const defaultConfig = localStorage.getItem("fasoo-eco-user");
        if (!defaultConfig) return;
        let defaultConfigJson = JSON.parse(defaultConfig);
        defaultConfigJson = {
          ...defaultConfigJson,
          ecoUserInfo: { ...info },
        };
        localStorage.setItem(
          "fasoo-eco-user",
          JSON.stringify(defaultConfigJson)
        );
      }
    );
    builder.addCase(
      trunkGetWorkspaceConfig.fulfilled,
      (state, { payload: config }) => {
        const defaultConfig = localStorage.getItem("fasoo-eco-user");
        if (!defaultConfig) return;
        let defaultConfigJson = JSON.parse(defaultConfig);
        defaultConfigJson = {
          ...defaultConfigJson,
          serviceConfig: {
            ...defaultConfigJson.serviceConfig,
            chatAvailibility: config.chatAvailibility ?? false,
            companyName: config.companyName ?? "",
          },
          ecoCommonConfig: {
            ...config,
            workgroupPermissionDTO: {
              ...config.workgroupPermissionDTO,
              policy: {
                ...config?.workgroupPermissionDTO?.policy,
                drmPerm: config?.workgroupPermissionDTO?.policy?.drmPerm ?? [],
              },
            },
          },
        };
        localStorage.setItem(
          "fasoo-eco-user",
          JSON.stringify(defaultConfigJson)
        );
      }
    );
    builder.addCase(trunkUpdateWorkspaceMember.fulfilled, (state, action) => {
      const info = action.payload;
      state.memberList = state.memberList?.map((item) =>
        item.userId === info.userId ? info : item
      );
      if (action.meta.arg.defaultTenancyCode) {
        state.workspaceSideList = state.workspaceSideList?.map((item) =>
          item.dsdCode === action.meta.arg.defaultTenancyCode
            ? { ...item, myDefaultTenancy: true }
            : { ...item, myDefaultTenancy: false }
        );
      }
    });
  },
});
export default workspaceSlice.reducer;
